import { useState, useRef, useEffect, useCallback } from 'react';
import AppIcon from '../AppIcon';

/**
 * SmartSelect — custom dropdown that renders its option list via `position: fixed`
 * so it is never clipped by any parent overflow constraint.
 *
 * Props mirror a basic <select>:
 *   value        – current selected value (string)
 *   onChange     – called with a synthetic { target: { value } } for drop-in compatibility
 *   options      – [{ value, label }]
 *   placeholder  – shown when nothing is selected
 *   required     – boolean, marks the trigger with aria-required
 *   className    – extra classes for the trigger button
 *   disabled     – boolean
 */
const SmartSelect = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'اختر...',
  required = false,
  className = '',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  const computeMenuStyle = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const MENU_ITEM_H = 44;
    const MENU_MAX_H = 220;
    const menuH = Math.min(options.length * MENU_ITEM_H + 8, MENU_MAX_H);
    const spaceBelow = window.innerHeight - rect.bottom - 6;
    const openAbove = spaceBelow < menuH && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      maxHeight: MENU_MAX_H,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [options.length]);

  const openMenu = () => {
    if (disabled) return;
    computeMenuStyle();
    setOpen(true);
  };

  const closeMenu = () => setOpen(false);

  const select = (val) => {
    onChange?.({ target: { value: val } });
    closeMenu();
  };

  /* Close on click-outside */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* Close on Escape, recompute on resize/scroll */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && closeMenu();
    const onResize = () => computeMenuStyle();
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, computeMenuStyle]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={open ? closeMenu : openMenu}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required}
        className={[
          'w-full h-11 rounded-xl border bg-white px-3 text-sm flex items-center justify-between gap-2',
          'focus:outline-none focus:ring-2 focus:ring-primary transition-colors',
          open ? 'border-primary ring-2 ring-primary' : 'border-slate-300 hover:border-slate-400',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          selected ? 'text-slate-900' : 'text-slate-400',
          className,
        ].join(' ')}
      >
        <span className="flex-1 text-right truncate">
          {selected ? selected.label : placeholder}
        </span>
        <AppIcon
          name="ChevronDown"
          size={16}
          className={`text-slate-400 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="listbox"
          style={menuStyle}
          className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-y-auto"
        >
          {placeholder && (
            <button
              type="button"
              role="option"
              aria-selected={!value}
              onClick={() => select('')}
              className="w-full text-right px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-50 transition-colors"
            >
              {placeholder}
            </button>
          )}
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={value === opt.value}
              onClick={() => select(opt.value)}
              className={[
                'w-full text-right px-4 py-2.5 text-sm transition-colors',
                value === opt.value
                  ? 'bg-primary/5 text-primary font-semibold'
                  : 'text-slate-800 hover:bg-slate-50',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default SmartSelect;
